import { LogOut, Sun, Moon } from 'lucide-react';
import { Link, useLocation, useMatch } from 'react-router-dom';
import { cn } from '@/lib/infra/utils';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuthStore } from '@/stores/authStore';
import { FOOTER_NAV_IDS, FOOTER_NAV_ORDER, type NavItem } from '@/types/navigation';
import { StatusBar } from './StatusBar';

interface AuthShellProps<V extends string> {
  navItems: NavItem<V>[];
  children: React.ReactNode;
  brandLetter?: string;
}

export function AuthShell<V extends string>({
  navItems,
  children,
  brandLetter = 'K',
}: AuthShellProps<V>) {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const currentView = useLocation().pathname.split('/')[1] || '';

  let lastSection: string | undefined;
  const mainNavItems = navItems.filter((item) => !FOOTER_NAV_IDS.has(item.id));
  const footerNavItems = navItems
    .filter((item) => FOOTER_NAV_IDS.has(item.id))
    .sort((a, b) => FOOTER_NAV_ORDER.indexOf(a.id) - FOOTER_NAV_ORDER.indexOf(b.id));

  return (
    <div data-view={currentView} className="bg-background text-foreground flex h-screen overflow-hidden">
      <aside className="bg-muted border-border/50 w-16 border-r">
        <div className="flex h-full flex-col">
          {/* Brand */}
          <div className="border-border flex h-12 items-center justify-center border-b">
            <span className="text-primary text-lg font-bold">{brandLetter}</span>
          </div>

          {/* Nav */}
          <nav className="flex-1 py-2">
            {mainNavItems.map((item) => {
              const showDivider = lastSection !== undefined && item.section !== lastSection;
              lastSection = item.section;
              return (
                <div key={item.id}>
                  {showDivider && <div className="border-border mx-3 my-1 border-t" />}
                  <NavLinkButton item={item} />
                </div>
              );
            })}
          </nav>

          {/* Footer: user, settings, theme, logout */}
          <div className="border-border space-y-1 border-t py-2">
            {user && (
              <div className="flex h-8 items-center justify-center">
                <span className="text-muted-foreground truncate text-[10px]">{user.username}</span>
              </div>
            )}
            {footerNavItems.map((item) => (
              <NavLinkButton key={item.id} item={item} />
            ))}
            <ThemeButton />
            <button
              onClick={() => logout()}
              title="Sign out"
              className={cn(
                'group relative flex h-12 w-full items-center justify-center transition-colors',
                'text-muted-foreground hover:text-destructive hover:bg-destructive/10',
              )}
            >
              <LogOut className="h-5 w-5" />
              <div className="bg-popover text-popover-foreground invisible absolute left-full z-50 ml-2 whitespace-nowrap rounded px-2 py-1 text-xs opacity-0 shadow-lg transition-all group-hover:visible group-hover:opacity-100">
                Sign out
              </div>
            </button>
          </div>
        </div>
      </aside>
      <main className="flex flex-1 flex-col overflow-hidden">
        <div className="flex-1 overflow-hidden">{children}</div>
        <StatusBar />
      </main>
    </div>
  );
}

// Single sidebar nav button. Active state comes from `useMatch` on the
// current location (not props), so navigation can be driven by the URL —
// browser back/forward, deep links, and right-click → "open in new tab"
// all work without anything else in the app needing to know.
function NavLinkButton<V extends string>({ item }: { item: NavItem<V> }) {
  const Icon = item.icon;
  // end:false so /positions/trader/123 still highlights /positions, etc.
  const match = useMatch({ path: item.path, end: false });
  const isActive = !!match;
  return (
    <Link
      to={item.path}
      data-nav-button={item.id}
      title={item.label}
      className={cn(
        'group relative flex h-12 w-full items-center justify-center transition-colors',
        !isActive && 'text-muted-foreground hover:text-foreground hover:bg-muted/50',
        isActive && 'text-primary bg-primary/10',
      )}
    >
      <Icon className="h-5 w-5" />
      {item.badge && (
        <span className="bg-muted-foreground/20 text-muted-foreground absolute bottom-1 right-1 rounded px-0.5 text-[7px] font-semibold uppercase leading-tight">
          {item.badge}
        </span>
      )}
      {isActive && (
        <div className="bg-primary absolute left-0 top-1/2 h-6 w-0.5 -translate-y-1/2 rounded-r" />
      )}
      <div className="bg-popover text-popover-foreground invisible absolute left-full z-50 ml-2 whitespace-nowrap rounded px-2 py-1 text-xs opacity-0 shadow-lg transition-all group-hover:visible group-hover:opacity-100">
        {item.label}
      </div>
    </Link>
  );
}

function ThemeButton() {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <button
      onClick={toggleTheme}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      className={cn(
        'group relative flex h-12 w-full items-center justify-center transition-colors',
        'text-muted-foreground hover:text-foreground hover:bg-muted/50',
      )}
    >
      {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
      <div className="bg-popover text-popover-foreground invisible absolute left-full z-50 ml-2 whitespace-nowrap rounded px-2 py-1 text-xs opacity-0 shadow-lg transition-all group-hover:visible group-hover:opacity-100">
        {isDark ? 'Light mode' : 'Dark mode'}
      </div>
    </button>
  );
}
